/**
 * Update Delivery Use Case
 *
 * This use case handles updating an existing delivery.
 * It follows the Use Case pattern for clean separation of business logic.
 *
 * @module UpdateDeliveryUseCase
 */

import {DistributionRepository} from '../repository/DistributionRepository';
import {DistributionRecord, UpdateDeliveryForm} from '../models/distribution.model';

export class UpdateDeliveryUseCase {
  constructor (private distributionRepository: DistributionRepository) {}

  async execute (id: string, updateData: UpdateDeliveryForm): Promise<DistributionRecord> {
    try {
      // Business Logic Validation
      if (!id || id.trim() === '') {
        throw new Error('Delivery ID is required');
      }

      if (!updateData) {
        throw new Error('Update data is required');
      }

      // Validate quantity if provided
      if (updateData.quantity !== undefined && updateData.quantity <= 0) {
        throw new Error('Quantity must be greater than 0');
      }

      // Validate serials if provided
      if (updateData.serials && updateData.serials.length > 0) {
        const validSerials = updateData.serials.filter(serial => serial.trim() !== '');
        if (validSerials.length === 0) {
          throw new Error('At least one valid serial must be provided');
        }
      }

      // Validate notes length if provided
      if (updateData.notes && updateData.notes.length > 500) {
        throw new Error('Notes must be less than 500 characters');
      }

      const result = await this.distributionRepository.updateDelivery(id, updateData);

      return result;
    } catch (error) {
      throw error;
    }
  }
}
